import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react';
import { validateForm, validateField, VALIDATION_RULES, ErrorMessage } from '../../../utils/formValidation';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const LoginForm = ({ onLogin, onForgotPassword, onOTPLogin, loading = false }) => {
  // Debug localStorage
  const rememberMeValue = localStorage.getItem('remember_me');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    emailOrPhone: '',
    rememberMe: rememberMeValue === 'true' // Pre-populate from localStorage
  });

  // Track if this is initial load or user interaction
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Update localStorage when rememberMe state changes (only on user interaction)
  useEffect(() => {

    // Skip localStorage update on initial load
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }

    // Only update localStorage on user interaction
    if (formData.rememberMe) {
      localStorage.setItem('remember_me', 'true');
    } else {
      localStorage.removeItem('remember_me');
    }
  }, [formData.rememberMe, isInitialLoad]);
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const getValidationRules = () => {
    if (loginMethod === 'password') {
      return {
        email: { ...VALIDATION_RULES.email, maxLength: 100 },
        password: VALIDATION_RULES.required
      };
    } else {
      return {
        emailOrPhone: {
          ...VALIDATION_RULES.email,
          maxLength: 100,
          customLabel: 'Email'
        }
      };
    }
  };

  const validateFieldLevel = (name, value) => {
    const rules = getValidationRules();
    if (rules[name]) {
      const fieldErrors = validateField(name, value, rules[name]);
      setErrors(prev => ({
        ...prev,
        [name]: fieldErrors.length > 0 ? fieldErrors[0] : ''
      }));
      return fieldErrors.length === 0;
    }
    return true;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateFieldLevel(name, value);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;


    if (name === 'email') {
      // For email field in password login, limit to 100 characters
      const emailValue = value.slice(0, 100);
      setFormData(prev => ({
        ...prev,
        [name]: emailValue
      }));
    } else if (name === 'emailOrPhone') {
      // For email in OTP login, limit to 100 characters
      const emailValue = value.slice(0, 100);
      setFormData(prev => ({
        ...prev,
        [name]: emailValue
      }));
    } else {
      const newValue = type === 'checkbox' ? checked : value;

      setFormData(prev => {
        const newFormData = {
          ...prev,
          [name]: newValue
        };
        return newFormData;
      });
    }

    // For real-time validation (optional, can be noisy)
    // We'll clear errors as they type, but validate format for things like phone numbers
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };


  const handleKeyDown = (e) => {
    const { name, value } = e.target;

    // Handle Enter key for form submission
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
      return;
    }

    // Prevent input if maximum length reached
    if (name === 'email' && value.length >= 100) {
      // Allow backspace, delete, arrow keys, tab, enter
      if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
      }
    } else if (name === 'password' && value.length >= 72) {
      // Allow backspace, delete, arrow keys, tab, enter
      if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
      }
    } else if (name === 'emailOrPhone') {
      if (value.length >= 100) {
        // Allow backspace, delete, arrow keys, tab, enter
        if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
          e.preventDefault();
        }
      }
    }
  };

  const handlePaste = (e) => {
    const { name } = e.target;

    if (name === 'email') {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const emailValue = pastedData.slice(0, 100);
      setFormData(prev => ({
        ...prev,
        [name]: emailValue
      }));
    } else if (name === 'password') {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const passwordValue = pastedData.slice(0, 72);
      setFormData(prev => ({
        ...prev,
        [name]: passwordValue
      }));
    } else if (name === 'emailOrPhone') {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');

      // For email, limit to 100 characters
      const emailValue = pastedData.slice(0, 100);
      setFormData(prev => ({
        ...prev,
        [name]: emailValue
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const rules = getValidationRules();
    const { errors: newErrors, isValid } = validateForm(formData, rules);

    // Customize error messages for OTP login
    if (loginMethod === 'otp' && newErrors.emailOrPhone) {
      const msg = newErrors.emailOrPhone;
      if (msg && typeof msg === 'string' && msg.includes('Email or Phone')) {
        newErrors.emailOrPhone = msg.replace('Email or Phone', 'Email');
      }
    }

    setErrors(newErrors);

    if (isValid) {
      toast.dismiss('auth-form-validation-error');
      if (loginMethod === 'password') {
        // Trim email before sending to prevent validation errors
        onLogin(formData.email.trim(), formData.password, formData.rememberMe);
      } else {
        // Trim emailOrPhone before sending to prevent validation errors
        onOTPLogin(formData.emailOrPhone.trim(), 'email', formData.rememberMe);
      }
    } else {
      const firstError = loginMethod === 'password'
        ? (newErrors.email || newErrors.password || 'Please enter a valid email and password.')
        : (newErrors.emailOrPhone || 'Please enter a valid work email address.');
      toast.error(firstError, { id: 'auth-form-validation-error' });
    }
  };

  const formVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      y: -8,
      transition: { duration: 0.2 },
    },
  };

  return (
    <div className="w-full font-sans">
      {/* Logo & Branding */}
      <div className="flex items-center gap-3.5 mb-7">
        <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
          <img 
            src="/mobiloitte-logo.png" 
            alt="Mobiloitte Logo" 
            className="w-full h-full object-contain"
          />
        </div>
        <div className="flex flex-col">
          <h2 className="text-xl font-bold text-hrms-primary tracking-tight leading-none mb-1">
            Mobiloitte
          </h2>
          <p className="text-[10px] font-bold text-hrms-muted tracking-[0.2em] uppercase leading-none">
            Technologies
          </p>
        </div>
      </div>

      {/* Enterprise Badge */}
      <div className="mb-5">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#0F2147]/5 text-hrms-primary border border-[#0F2147]/15">
          <svg className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 2h5l1 1h6v10H2V2zm1 1v9h10V4H7.5L6.5 3H3z"/>
            <rect x="5" y="6" width="2" height="2" rx="0.5"/>
            <rect x="9" y="6" width="2" height="2" rx="0.5"/>
            <rect x="5" y="9" width="2" height="2" rx="0.5"/>
            <rect x="9" y="9" width="2" height="2" rx="0.5"/>
          </svg>
          Enterprise HRMS
        </span>
      </div>

      {/* Welcome Heading */}
      <div className="mb-1">
        <h1 className="text-2xl lg:text-[28px] font-bold text-hrms-text leading-tight tracking-tight">
          Welcome Back
        </h1>
      </div>
      <p className="text-hrms-muted text-[13px] leading-relaxed mb-6 max-w-xs">
        Sign in to access your HRMS dashboard, attendance, payroll, leave management, projects and employee services.
      </p>

      {/* Login Method Tabs */}
      <div className="mb-5">
        <div className="flex bg-gray-100 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setLoginMethod('password')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setLoginMethod('password');
              }
            }}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-[10px] transition-all duration-200 ${
              loginMethod === 'password'
                ? 'bg-white text-hrms-primary shadow-sm'
                : 'text-hrms-muted hover:text-hrms-text'
            }`}
            id="login-tab-password"
            role="tab"
            aria-selected={loginMethod === 'password'}
          >
            Password
          </button>
          <button
            type="button"
            onClick={() => setLoginMethod('otp')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setLoginMethod('otp');
              }
            }}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-[10px] transition-all duration-200 ${
              loginMethod === 'otp'
                ? 'bg-white text-hrms-primary shadow-sm'
                : 'text-hrms-muted hover:text-hrms-text'
            }`}
            id="login-tab-otp"
            role="tab"
            aria-selected={loginMethod === 'otp'}
          >
            OTP
          </button>
        </div>
      </div>

      <form noValidate onSubmit={handleSubmit} className="space-y-4" autoComplete="off" data-form-type="other">
        <AnimatePresence mode="wait">
          {loginMethod === 'password' ? (
            <motion.div
              key="password-fields"
              variants={formVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="space-y-4"
            >
              {/* Email Field */}
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-hrms-text mb-1.5">
                  Email
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-600 transition-colors pointer-events-none" />
                  <input
                    id="login-email"
                    type="text"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    maxLength={100}
                    autoComplete="off"
                    data-form-type="other"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    className={`login-input ${errors.email ? 'login-input-error' : ''}`}
                    placeholder="Work Email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500 font-sans">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-hrms-text mb-1.5">
                  Password
                </label>
                <div className="relative group">
                  <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-600 transition-colors pointer-events-none" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    maxLength={72}
                    autoComplete="new-password"
                    data-form-type="other"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    className={`login-input pr-10 ${errors.password ? 'login-input-error' : ''}`}
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowPassword(!showPassword);
                      }
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    id="login-toggle-password"
                  >
                    {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-red-500 font-sans">{errors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer group" htmlFor="login-remember-me">
                  <input
                    id="login-remember-me"
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition"
                  />
                  <span className="ml-2 text-sm text-hrms-muted group-hover:text-hrms-text transition-colors">Remember Me</span>
                </label>
                <button
                  type="button"
                  onClick={() => onForgotPassword(formData.email)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onForgotPassword(formData.email);
                    }
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  id="login-forgot-password"
                >
                  Forgot Password?
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="otp-fields"
              variants={formVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="space-y-4 py-1"
            >
              {/* Email Input Field for OTP */}
              <div>
                <label htmlFor="login-otp-email" className="block text-sm font-medium text-hrms-text mb-1.5">
                  Email
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-600 transition-colors pointer-events-none" />
                  <input
                    id="login-otp-email"
                    type="text"
                    name="emailOrPhone"
                    value={formData.emailOrPhone}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    maxLength={100}
                    autoComplete="off"
                    data-form-type="other"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    className={`login-input ${errors.emailOrPhone ? 'login-input-error' : ''}`}
                    placeholder="Work Email"
                  />
                </div>
                {errors.emailOrPhone && (
                  <p className="mt-1 text-xs text-red-500 font-sans">{errors.emailOrPhone}</p>
                )}
              </div>

              {/* Informational Message to balance height and provide UX clarity */}
              <div className="p-3 rounded-xl bg-blue-50/80 border border-blue-100 flex items-start gap-2.5">
                <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0 mt-1.5" />
                <p className="text-xs text-blue-900/80 font-sans leading-relaxed">
                  We'll send a 6-digit verification code to your registered work email address.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="login-btn-primary flex items-center justify-center gap-2 mt-2"
          id="login-submit-btn"
        >
          {loading && <Loader2 className="animate-spin h-4 w-4" />}
          {loginMethod === 'password' ? 'Login' : 'Send OTP'}
        </button>
      </form>

      {/* Help Text */}
      <div className="mt-6 pt-4 border-t border-gray-100 text-center">
        <p className="text-xs text-hrms-muted">
          Need help?{' '}
          <a
            href={`mailto:${process.env.REACT_APP_HR_EMAIL || 'hr@mobiloitte.com'}`}
            className="text-hrms-primary hover:text-[#0B2033] font-medium transition-colors"
            id="login-contact-hr"
          >
            Contact HR Administrator
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
